import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AlertEvent, AlertsState } from '@/types/alerts';

const initialState: AlertsState = {
  activeAlerts: [],
  resolvedAlerts: [],
  isConnected: false,
  error: null,
  lastUpdated: null,
  hasUnviewedAlerts: false,
};

const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    /**
     * Initialize alerts from stream init event
     */
    initializeAlerts(state, action: PayloadAction<AlertEvent[]>) {
      state.activeAlerts = action.payload.filter(a => a.isActive);
      state.resolvedAlerts = action.payload
        .filter(a => !a.isActive)
        .slice(0, 50);
      state.lastUpdated = new Date().toISOString();
    },

    /**
     * Add or update an active alert
     */
    addAlert(state, action: PayloadAction<AlertEvent>) {
      const alert = action.payload;
      const existingIndex = state.activeAlerts.findIndex(
        a => a.id === alert.id
      );

      if (existingIndex >= 0) {
        // Update existing alert
        state.activeAlerts[existingIndex] = alert;
      } else {
        // Add new alert
        state.activeAlerts.unshift(alert);
        state.hasUnviewedAlerts = true; // Mark as unviewed when new alert arrives
      }

      state.lastUpdated = new Date().toISOString();
    },

    /**
     * Move alert from active to resolved
     */
    resolveAlert(state, action: PayloadAction<string>) {
      const alertId = action.payload;
      const alertIndex = state.activeAlerts.findIndex(a => a.id === alertId);

      if (alertIndex >= 0) {
        const [resolvedAlert] = state.activeAlerts.splice(alertIndex, 1);
        resolvedAlert.isActive = false;

        // Add to resolved history (keep max 50)
        state.resolvedAlerts.unshift(resolvedAlert);
        if (state.resolvedAlerts.length > 50) {
          state.resolvedAlerts.pop();
        }
      }

      state.lastUpdated = new Date().toISOString();
    },

    /**
     * Dismiss alert (remove from active view without resolving)
     */
    dismissAlert(state, action: PayloadAction<string>) {
      const alertId = action.payload;
      state.activeAlerts = state.activeAlerts.filter(a => a.id !== alertId);
      state.lastUpdated = new Date().toISOString();
    },

    /**
     * Clear all active alerts
     */
    clearAllAlerts(state) {
      state.activeAlerts = [];
      state.lastUpdated = new Date().toISOString();
    },

    /**
     * Set error message
     */
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },

    /**
     * Clear error message
     */
    clearError(state) {
      state.error = null;
    },

    /**
     * Set connection status
     */
    setConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    /**
     * Mark alerts as viewed (clear the notification indicator)
     */
    markAlertsAsViewed(state) {
      state.hasUnviewedAlerts = false;
    },

    /**
     * Reset alerts state
     */
    reset() {
      return initialState;
    },
  },
});

export const {
  initializeAlerts,
  addAlert,
  resolveAlert,
  dismissAlert,
  clearAllAlerts,
  setError,
  clearError,
  setConnected,
  markAlertsAsViewed,
  reset,
} = alertsSlice.actions;

export default alertsSlice.reducer;
